import { Box, Grid } from "@mui/material";
import { useAppSelector } from "app/hooks";
import { AdvancedSettingsBar } from "components/Campaign/CampaignEffects/AdvancedSettingsBar/AdvancedSettingsBar";
import { useMessage } from "hooks/intl";
import { IndexItem } from "IndexItem";
import { isUndefined } from "lodash";
import { Fragment } from "react";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import {
  AchievementAggregation,
  AchievementAggregationUnion,
  AchievementRuleTypes,
  AchievementRuleTypeUnion,
  AchievementTriggers,
  AchievementTriggersUnion,
  BEAchievementRule,
} from "types/achievements";
import { GoalDescription } from "../AchievementForm/Rules/RuleGoal/RuleGoalReadOnly";
import { parseToFEPeriod } from "../helpers";
import { Conditions } from "./Conditions";
import { AchievementLimit } from "./Limit";
import { useStyles } from "./SingleAchievement.styles";

interface Props {
  achievementData?: { rules?: BEAchievementRule[] };
}

export const Rules = ({ achievementData }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();

  const handleAchievementType = (type: AchievementRuleTypeUnion) => {
    switch (type) {
      case AchievementRuleTypes.DIRECT:
        return message({ id: "directAchievement" });
      case AchievementRuleTypes.REFERRAL:
        return message({ id: "referralAchievement" });
      default:
        return type;
    }
  };

  const handleAchievementTrigger = (
    trigger: AchievementTriggersUnion,
    schema?: string,
  ) => {
    switch (trigger) {
      case AchievementTriggers.TRANSACTION:
        return message({ id: "transaction" });
      case AchievementTriggers.CUSTOM_EVENT:
        return schema
          ? message({ id: "customEventWithSchema" }, { schema: schema })
          : message({ id: "customEvent" });
      default:
        return trigger;
    }
  };

  const handleAchievementCounting = (
    aggregationType: AchievementAggregationUnion,
  ) => {
    switch (aggregationType) {
      case AchievementAggregation.EXPRESSION:
        return message({ id: "numberOfSpecificEventAttributes" });
      case AchievementAggregation.QUANTITY:
        return message({ id: "numberOfEventOccurrences" });
      default:
        return aggregationType;
    }
  };

  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const { code } = defaultLang;

  const getCustomEventTriggerLabels = (rule: BEAchievementRule) => [
    {
      label: message({ id: "countUniqueCustomEventAttributes" }),
      value: "",
    },
    {
      label: message({ id: "customEventAttribute" }),
      value: rule?.completeRule?.uniqueAttribute || "",
    },
  ];

  const rulesItems =
    achievementData?.rules?.map((rule, ruleIndex: number) => [
      {
        left: (
          <span className={classes.rulesHeader}>
            <span className={classes.ruleName}>
              {rule?.translations?.[code]?.name ||
                message({ id: "ruleNumber" }, { number: ruleIndex + 1 })}
            </span>
            <span className={classes.ruleDescription}>
              {rule?.translations?.[code]?.description}
            </span>
          </span>
        ),
        leftDataTestId: "rule-label",
        right: "",
        rightDataTestId: "rule-value",
      },
      {
        left: "achievementType",
        leftDataTestId: "achievement-type-label",
        right: handleAchievementType(rule?.type),
        rightDataTestId: "achievement-type-value",
      },
      {
        left: "achievementTrigger",
        leftDataTestId: "achievement-trigger-label",
        right: handleAchievementTrigger(rule?.trigger, rule?.event),
        rightDataTestId: "achievement-trigger-value",
      },
      {
        left: "achievementCounting",
        leftDataTestId: "achievement-counting-label",
        right: handleAchievementCounting(rule?.aggregation?.type),
        rightDataTestId: "achievement-counting-value",
      },
      {
        left: "eventFrequencyLimitPerMember",
        leftDataTestId: "event-frequency-limit-per-member-label",
        right: <AchievementLimit {...rule?.limit} />,
        rightDataTestId: "event-frequency-limit-per-member-value",
      },
      {
        left: "achievementGoal",
        leftDataTestId: "achievement-goal-label",
        right: (
          <Box display="block">
            <GoalDescription
              completeRule={{
                ...rule.completeRule,
                period: parseToFEPeriod(rule.completeRule.period),
              }}
              trigger={rule?.trigger}
              event={rule?.event}
              aggregation={rule?.aggregation}
            />

            {!!rule?.completeRule?.uniqueAttribute && (
              <Box mt={10}>
                <AdvancedSettingsBar
                  variant="text"
                  labels={getCustomEventTriggerLabels(rule)}
                  vectorMarginClass={classes.vectorMargin}
                />
              </Box>
            )}
          </Box>
        ),
        rightDataTestId: "achievement-goal-value",
      },
      ...(rule?.aggregation?.rule
        ? [
            {
              left: "targetAttribute",
              leftDataTestId: "target-attribute-label",
              right: (
                <IndexItem className={classes.chips}>
                  {rule?.aggregation?.rule
                    ?.replaceAll("<#>", "")
                    ?.replaceAll("</#>", "")}
                </IndexItem>
              ),
              rightDataTestId: "target-attribute-value",
            },
          ]
        : []),
      ...(rule?.conditions?.length
        ? [
            {
              isConditions: true,
              left: rule?.conditions?.map((condition: any, index: number) => (
                <span className={classes.ruleWrapper}>
                  <span>{index + 1}.</span>
                  <Conditions condition={condition} schema={rule?.event} />
                </span>
              )),
              leftDataTestId: "conditions-label",
              right: "",
              rightDataTestId: "conditions-value",
            },
          ]
        : []),
    ]) || [];

  return (
    <>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <span className={classes.header}>{message({ id: "rules" })}</span>
          {rulesItems.map((row) =>
            row.map((item, index: number) => (
              <Fragment key={index}>
                {item.isConditions && (
                  <span className={classes.conditionsInfo}>
                    {message({ id: "singleAchievementInfo" })}
                  </span>
                )}
                <span
                  className={`${classes.content} ${index === 0 ? classes.firstItem : ""}`}
                >
                  <span
                    className={item.isConditions ? classes.rules : classes.left}
                    data-test-id={item.leftDataTestId}
                  >
                    {typeof item.left === "string"
                      ? message({ id: item.left })
                      : item.left}
                  </span>
                  <span
                    className={classes.right}
                    data-test-id={item.rightDataTestId}
                  >
                    {isUndefined(item.right) ? "-" : item.right}
                  </span>
                </span>
              </Fragment>
            )),
          )}
        </Grid>
      </Grid>
    </>
  );
};
